import { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import useModelEstimation from '../ModelEstimation/useModelEstimation';
import usePerformanceTesting from '../ModelTesting/usePerformanceTesting';
import useFeatureEngineering from '../FeatureEngineering/useFeatureEngineering';
import useSampling from '../Sampling/useSampling';
import useVariableReduction from '../VR/useVariableReduction';
import useTreatingOutliers from '../EDA/useTreatingOutliers';
// import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import useModelDeployment from '../ModelDeployment/useModelDeployment';
import useModelComparison from '../ModelTesting/useModelComparison';

const ME_BASE_URL = ApiEndpoints.MODULE_BASE_URL.ME;

/**
 * @function useNormalOperations
 * @description function for fetching data for normal operations
 * @param {String} operationKey operation key for fetching data
 * @param {Object} activeDataset active dataset object
 * @param {String} moduleKey module key for fetching data
 * @param {String} subModuleKey sub module key for fetching data
 * @param {Object} activeProject active project object
 * @param {String} moduleObjKey module object key for fetching data
 * @param {String} subModuleObjKey sub module object key for fetching data
 * @param {String} operationObjKey operation object key for fetching data
 * @returns {Object} object containing data for normal operations
 */
export default function useNormalOperations({
  operationKey,
  activeDataset,
  moduleKey,
  subModuleKey,
  activeProject,
  moduleObjKey,
  subModuleObjKey,
  operationObjKey,
}) {
  const { userData } = useSelector((state) => state.user);
  const {
    fetchBigDataStatus,
    savedModels,
    fetchSavedModelList,
    renameModelDisplayName,
    deploySavedModel,
    deploySavedModelWithConfig,
  } = useModelEstimation();

  const { fetchMetricData } = useModelComparison();

  const { fetchAllModelDetails, allModelData } = usePerformanceTesting();
  const {
    binningColumn,
    updatebinningID,
    fetchBinningColumnDetails,
    fetchOrdinalColumnDetails,
    ordinalColumn,
  } = useFeatureEngineering();
  const { fetchResamplingMethods } = useSampling();
  const {
    finalSubmit,
    // fetchVariableFinalisation,
    // sendVariableFinalisation,
    // variableFinalisation,
  } = useVariableReduction();
  const { idx } = useTreatingOutliers();
  const { allMETasks, fetchMEModuleTasks } = useModelDeployment();
  const { datasetList, datasetRowsWithPagination, fetchDatasetList } =
    useProject();
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);
  const [targetVariables, setTargetVariables] = useState([]);
  const fetchDatasetAllColumnDetails = (datasetName, mKey) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL?.[mKey] +
          ApiEndpoints?.[mKey]?.DATASET_COLUMN_DETAILS
        }`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data?.columns);
          return res;
        },
        (error) => {
          throw error;
        }
      );

  const fetchTargetVariables = (mKey, datasetName) => {
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL?.[mKey] +
          ApiEndpoints?.[mKey]?.GET_TARGET_LIST
        }`,
        datasetName
      )
      .then(
        (res) => {
          setTargetVariables(res.data.valid_targets);
        },
        (error) => {
          throw error;
        }
      );
  };
  /**
   * @function fetchModelsDetails
   * @description API call to get the model details.
   * @param {number} modelId - modelId of the active project.
   * @param {function} successCallback - callback function  for successful request.
   * @returns {Promise} AxiosPromise
   * @example fetchModelsDetails(projectId,successCallback);
   */

  const fetchModelsDetails = (modelId, successCallback) => {
    if (modelId) {
      axios
        .get(
          `${ME_BASE_URL}${ApiEndpoints.MT.MT_ALL_MODELS_DETAILS}?model_id=${modelId}`
        )
        .then(
          (res) => {
            successCallback(res);
          },
          () => {}
        );
    }
  };
  useEffect(() => {
    const datasetDetailsPayload = {
      dataset_id: activeDataset?.id,
      project_id: activeProject?.project_id,
    };
    if (activeDataset?.name) {
      setTimeout(() => {
        fetchDatasetList(activeProject?.project_id);
        fetchAllModelDetails(
          activeProject.project_id,
          [
            'id',
            'model_name',
            'model_technique',
            'model_technique_label',
            'target_column',
            'train_table',
            'archive',
            'external_pkl',
            'status',
            'library',
          ].join()
        );
      }, 1000);
      if (
        moduleObjKey !== 'MD' &&
        moduleObjKey !== 'SS' &&
        moduleObjKey !== 'FE'
      ) {
        fetchDatasetAllColumnDetails(datasetDetailsPayload, moduleObjKey);
        fetchTargetVariables(moduleObjKey, datasetDetailsPayload);
      }
      if (moduleObjKey === 'SS' && subModuleObjKey === 'SA') {
        fetchDatasetAllColumnDetails(datasetDetailsPayload, 'SAMPLING');
        fetchTargetVariables('SAMPLING', datasetDetailsPayload);
      }
      if (moduleObjKey === 'SS' && subModuleObjKey === 'SE') {
        fetchDatasetAllColumnDetails(datasetDetailsPayload, 'SEG');
        fetchTargetVariables('SEG', datasetDetailsPayload);
      }
      if (moduleObjKey === 'FE') {
        fetchOrdinalColumnDetails(activeDataset?.id, activeProject?.project_id);
        fetchDatasetAllColumnDetails(datasetDetailsPayload, 'FTS');
        fetchTargetVariables('FTS', datasetDetailsPayload);
      }
      fetchBinningColumnDetails(datasetDetailsPayload);
    }
    if (
      !activeDataset?.name &&
      datasetColumnDetails?.length &&
      targetVariables?.length
    ) {
      setDatasetColumnDetails([]);
      setTargetVariables([]);
    }
  }, [
    moduleKey,
    subModuleKey,
    operationKey,
    activeDataset,
    moduleObjKey,
    subModuleObjKey,
    operationObjKey,
  ]);

  return {
    fetchBigDataStatus,
    datasetColumnDetails,
    datasetList,
    allModelData,
    datasetRowsWithPagination,
    userData,
    targetVariables,
    fetchDatasetAllColumnDetails,
    idx,
    binningColumn,
    ordinalColumn,
    updatebinningID,
    fetchResamplingMethods,
    finalSubmit,
    // fetchVariableFinalisation,
    // sendVariableFinalisation,
    // variableFinalisation,
    fetchDatasetList,
    savedModels,
    fetchSavedModelList,
    renameModelDisplayName,
    deploySavedModel,
    deploySavedModelWithConfig,
    allMETasks,
    fetchMEModuleTasks,
    fetchModelsDetails,
    fetchMetricData,
  };
}
